import React from 'react';
import { PageHeader, ScrollReveal, StatsBar, CTASection } from '../components/UI';
import IMAGES from '../images';

const Portfolio = () => {
  const caseStudies = [
    {
      name: 'A*** Roofing Co.',
      industry: 'Roofing Contractor',
      location: 'Texas, USA',
      image: IMAGES.caseStudy1,
      stats: { clicks: '1.17K', impressions: '82.8K', ctr: '1.4%', position: '9.1' },
      period: '3 Months',
      highlight: '+312% Traffic Growth',
      story: 'This roofing company was struggling to generate leads online. Their website was barely visible in search results, and they were spending money on Google Ads with poor results. Within 3 months of working with us, we increased their organic traffic by 312% and they\'re now booking 15+ estimates per week from Google alone.',
      services: ['Local SEO', 'Google Ads', 'GBP Optimization'],
      results: ['312% increase in organic traffic', '15+ weekly estimates from Google', 'Top 10 rankings for 25+ keywords', '45% reduction in cost-per-lead']
    },
    {
      name: 'B*** Home Services',
      industry: 'HVAC & Plumbing',
      location: 'California, USA',
      image: IMAGES.caseStudy2,
      stats: { clicks: '26.7K', impressions: '210K', ctr: '12.7%', position: '15.3' },
      period: '3 Months',
      highlight: '12.7% CTR Achievement',
      story: 'Achieved an exceptional 12.7% click-through rate—more than 6x the industry average. Our optimized meta descriptions and strategic keyword targeting made their listings irresistible to searchers looking for HVAC and plumbing services in their area.',
      services: ['Technical SEO', 'Content Optimization', 'Local SEO'],
      results: ['12.7% CTR (6x industry average)', '26.7K organic clicks', '210K search impressions', 'Dominance in local service searches']
    },
    {
      name: 'C*** Contractors',
      industry: 'General Contractor',
      location: 'Florida, USA',
      image: IMAGES.caseStudy3,
      stats: { clicks: '19K', impressions: '196K', ctr: '9.6%', position: '3.6' },
      period: '7 Days',
      highlight: 'Position 3.6 Average',
      story: 'Ranking in the top 4 positions across their target keywords. This contractor now dominates search results in their service area and has had to hire additional crews to handle the increased demand from online leads.',
      services: ['Local SEO', 'Google Ads', 'Review Management'],
      results: ['Average position 3.6 in search results', '19K organic clicks', 'Hired 2 additional crews', '196K monthly impressions']
    },
    {
      name: 'D*** Property Management',
      industry: 'Property Management',
      location: 'New York, USA',
      image: IMAGES.caseStudy4,
      stats: { clicks: '42.7K', impressions: '667K', ctr: '6.4%', position: '6.2' },
      period: '3 Months',
      highlight: '667K Impressions',
      story: 'Generated over 667,000 impressions in a highly competitive New York market. Their brand visibility skyrocketed, leading to a 280% increase in qualified property management inquiries from both property owners and potential tenants.',
      services: ['SEO Strategy', 'Content Marketing', 'Google Ads'],
      results: ['667K search impressions', '42.7K organic clicks', '280% increase in inquiries', 'Market leader visibility in NYC']
    },
    {
      name: 'E*** Auto Glass',
      industry: 'Auto Services',
      location: 'Arizona, USA',
      image: IMAGES.caseStudy5,
      stats: { clicks: '5.79K', impressions: '73K', ctr: '7.9%', position: '5.6' },
      period: '7 Days',
      highlight: 'Top 6 Rankings',
      story: 'Consistently ranking in the top 6 for high-intent auto glass keywords. Their phone now rings with customers ready to book, not just tire-kickers looking for quotes. Quality of leads improved dramatically.',
      services: ['Local SEO', 'GBP Optimization', 'Review Generation'],
      results: ['Top 6 rankings for target keywords', '7.9% click-through rate', 'Higher quality leads', 'Reduced quote-to-booking ratio']
    },
    {
      name: 'F*** Dental Care',
      industry: 'Dental Practice',
      location: 'Ohio, USA',
      image: IMAGES.caseStudy6,
      stats: { clicks: '9.7K', impressions: '39.5K', ctr: '24.5%', position: '5.4' },
      period: '7 Days',
      highlight: '24.5% CTR Achievement',
      story: 'An incredible 24.5% CTR—one of the highest we\'ve ever achieved. This dental practice went from struggling to fill appointments to having a 3-week waiting list for new patients. Their online reputation and visibility transformed completely.',
      services: ['Local SEO', 'Review Management', 'Content Strategy'],
      results: ['24.5% CTR (highest in portfolio)', '3-week waiting list', 'Full appointment calendar', 'Dominant local presence']
    },
  ];

  return (
    <div>
      <PageHeader 
        title="Our Portfolio"
        subtitle="Real results for real businesses. These aren't vanity metrics—these are actual Google Search Console screenshots from client campaigns."
        breadcrumb="Portfolio"
      />

      <StatsBar />

      {/* Case Studies Grid */}
      <section style={{ padding: '80px 24px', background: '#f8f9fa' }}>
        <div style={{ maxWidth: 1400, margin: '0 auto' }}>
          <ScrollReveal>
            <div style={{ textAlign: 'center', marginBottom: 50 }}>
              <span style={{ color: '#ff6b35', fontWeight: 600, fontSize: 13, textTransform: 'uppercase', letterSpacing: 2 }}>Case Studies</span>
              <h2 style={{ fontSize: 'clamp(28px, 4vw, 42px)', fontWeight: 800, color: '#1a1a2e', marginTop: 10, marginBottom: 16 }}>
                Success Stories
              </h2>
              <p style={{ color: '#666', maxWidth: 700, margin: '0 auto', fontSize: 17, lineHeight: 1.7 }}>
                Every business is different, but the results speak for themselves. Here's how we've helped local businesses dominate their markets.
              </p>
            </div>
          </ScrollReveal>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 60 }}>
            {caseStudies.map((study, i) => (
              <ScrollReveal key={i} delay={0.1}>
                <div style={{ 
                  background: 'white', 
                  borderRadius: 24, 
                  overflow: 'hidden',
                  boxShadow: '0 10px 40px rgba(0,0,0,0.08)',
                  display: 'grid',
                  gridTemplateColumns: i % 2 === 0 ? '1fr 1.2fr' : '1.2fr 1fr',
                }}>
                  <div style={{ 
                    order: i % 2 === 0 ? 0 : 1,
                    minHeight: 400,
                    position: 'relative'
                  }}>
                    <img src={study.image} alt={study.name} style={{ 
                      position: 'absolute',
                      top: 0, left: 0, width: '100%', height: '100%', 
                      objectFit: 'cover' 
                    }} />
                    <div style={{
                      position: 'absolute', top: 20, left: 20,
                      background: '#ff6b35', color: 'white',
                      padding: '8px 16px', borderRadius: 20,
                      fontSize: 13, fontWeight: 600
                    }}>
                      {study.period}
                    </div>
                  </div>
                  <div style={{ padding: 48, order: i % 2 === 0 ? 1 : 0 }}>
                    <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
                      {study.services.map((service, j) => (
                        <span key={j} style={{
                          background: 'rgba(255,107,53,0.1)', color: '#ff6b35',
                          padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 600
                        }}>
                          {service}
                        </span>
                      ))}
                    </div>
                    <h3 style={{ fontSize: 28, fontWeight: 800, color: '#1a1a2e', marginBottom: 8 }}>{study.name}</h3>
                    <p style={{ color: '#888', fontSize: 15, marginBottom: 20 }}>{study.industry} • {study.location}</p>
                    
                    <div style={{
                      background: 'linear-gradient(135deg, #ff6b35, #f54e00)',
                      borderRadius: 12, padding: 16, marginBottom: 24
                    }}>
                      <div style={{ color: 'white', fontWeight: 700, fontSize: 22, textAlign: 'center' }}>
                        {study.highlight}
                      </div>
                    </div>
                    
                    <p style={{ color: '#555', fontSize: 16, lineHeight: 1.8, marginBottom: 24 }}>
                      {study.story}
                    </p>
                    
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 24 }}>
                      {Object.entries(study.stats).map(([key, val]) => (
                        <div key={key} style={{ textAlign: 'center', padding: 12, background: '#f8f9fa', borderRadius: 10 }}>
                          <div style={{ fontSize: 20, fontWeight: 700, color: '#1a1a2e' }}>{val}</div>
                          <div style={{ fontSize: 11, color: '#888', textTransform: 'uppercase' }}>{key}</div>
                        </div>
                      ))}
                    </div>
                    
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 700, color: '#1a1a2e', marginBottom: 12 }}>KEY RESULTS:</div>
                      <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                        {study.results.map((result, j) => (
                          <li key={j} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8, color: '#555', fontSize: 15 }}>
                            <span style={{ color: '#ff6b35', fontSize: 18 }}>✓</span> {result}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Industries We Serve */}
      <section style={{ padding: '80px 24px', background: 'white' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          <ScrollReveal>
            <div style={{ textAlign: 'center', marginBottom: 50 }}>
              <span style={{ color: '#ff6b35', fontWeight: 600, fontSize: 13, textTransform: 'uppercase', letterSpacing: 2 }}>Industries</span>
              <h2 style={{ fontSize: 'clamp(28px, 4vw, 42px)', fontWeight: 800, color: '#1a1a2e', marginTop: 10, marginBottom: 16 }}>
                Industries We Specialize In
              </h2>
              <p style={{ color: '#666', maxWidth: 600, margin: '0 auto', fontSize: 17 }}>
                Our strategies are battle-tested across these industries with proven track records of success.
              </p>
            </div>
          </ScrollReveal>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 20 }}>
            {[
              { icon: '🏠', name: 'Roofing', desc: 'Contractors & installers' },
              { icon: '❄️', name: 'HVAC', desc: 'Heating & cooling' },
              { icon: '🔧', name: 'Plumbing', desc: 'Residential & commercial' },
              { icon: '🦷', name: 'Dental', desc: 'Practices & clinics' },
              { icon: '⚖️', name: 'Legal', desc: 'Law firms' },
              { icon: '🏢', name: 'Property Mgmt', desc: 'Management companies' },
              { icon: '🚗', name: 'Auto Services', desc: 'Repair & glass' },
              { icon: '🌿', name: 'Landscaping', desc: 'Lawn & garden' },
            ].map((industry, i) => (
              <ScrollReveal key={i} delay={i * 0.05}>
                <div style={{ textAlign: 'center', padding: 28, background: '#f8f9fa', borderRadius: 16 }}>
                  <div style={{ fontSize: 40, marginBottom: 12 }}>{industry.icon}</div>
                  <h3 style={{ fontSize: 18, fontWeight: 700, color: '#1a1a2e', marginBottom: 4 }}>{industry.name}</h3>
                  <p style={{ color: '#888', fontSize: 14, margin: 0 }}>{industry.desc}</p>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      <CTASection 
        title="Ready to Become Our Next Success Story?"
        subtitle="Join 50+ businesses that have transformed their online presence with Crescit Marketing."
      />
    </div>
  );
};

export default Portfolio;
